import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  Sparkles,
  MapPin,
  Home,
  BedDouble,
  Bath,
  Users,
  Wifi,
  Car,
  Trees,
  Sun,
  Waves,
  Flame,
  Snowflake,
  Sailboat,
  Bike,
  Dog,
  Tv,
  WashingMachine,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import { simulate } from "@/lib/api";
import { toast } from "sonner";

// --- Step definitions ---
const PROPERTY_TYPES = [
  { value: "Studio", label: "Studio", icon: Home },
  { value: "Appartement", label: "Appartement", icon: Home },
  { value: "Maison", label: "Maison", icon: Home },
  { value: "Villa", label: "Villa", icon: Home },
];

const AMENITIES = [
  { key: "vue_mer", label: "Vue mer", icon: Waves, premium: true },
  { key: "piscine", label: "Piscine", icon: Sailboat, premium: true },
  { key: "jacuzzi", label: "Jacuzzi", icon: Flame, premium: true },
  { key: "jardin", label: "Jardin", icon: Trees },
  { key: "terrasse", label: "Terrasse", icon: Sun },
  { key: "balcon", label: "Balcon", icon: Sun },
  { key: "parking", label: "Parking", icon: Car },
  { key: "garage", label: "Garage", icon: Car },
  { key: "cheminee", label: "Cheminée", icon: Flame },
  { key: "climatisation", label: "Climatisation", icon: Snowflake },
  { key: "wifi", label: "Wi-Fi", icon: Wifi },
  { key: "lave_vaisselle", label: "Lave-vaisselle", icon: WashingMachine },
  { key: "lave_linge", label: "Lave-linge", icon: WashingMachine },
  { key: "tv", label: "TV", icon: Tv },
  { key: "velos", label: "Vélos", icon: Bike },
  { key: "animaux_acceptes", label: "Animaux", icon: Dog },
];

const formatEUR = (n) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(n || 0);

const RevenueSimulator = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    postal_code: "",
    custom_city: "",
    property_type: "Appartement",
    bedrooms: 2,
    bathrooms: 1,
    capacity: 4,
    amenities: ["wifi"],
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleAmenity = (key) =>
    setForm((f) => ({
      ...f,
      amenities: f.amenities.includes(key)
        ? f.amenities.filter((a) => a !== key)
        : [...f.amenities, key],
    }));

  const canNext1 = /^\d{5}$/.test(form.postal_code.trim());
  const canNext2 = form.bedrooms >= 0 && form.bathrooms >= 0 && form.capacity >= 1;

  const submitSim = async () => {
    setLoading(true);
    setResult(null);
    try {
      const data = await simulate(form);
      setResult(data);
      setStep(4);
    } catch (e) {
      toast.error("Une erreur est survenue. Merci de réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const restart = () => {
    setResult(null);
    setStep(1);
  };

  return (
    <section
      id="simulator"
      className="py-24 lg:py-32 bg-gradient-to-b from-cream via-sand to-cream relative overflow-hidden"
      data-testid="simulator-section"
    >
      {/* Decorative blobs */}
      <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-gold/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-sage/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="container-tm relative">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="overline inline-flex items-center gap-2">
            <Sparkles size={12} strokeWidth={2} className="text-gold" />
            Simulation intelligente
          </span>
          <h2 className="mt-5 font-serif font-light text-ink text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
            Calculez vos
            <span className="italic text-gold"> revenus locatifs</span>.
          </h2>
          <p className="mt-6 font-sans text-base text-ink/65 leading-relaxed">
            Quelques clics suffisent. Notre algorithme analyse le marché breton
            local et vous propose une fourchette personnalisée, accompagnée
            d'une analyse rédigée par notre IA.
          </p>
        </div>

        {/* Progress bar */}
        {step < 4 && (
          <div className="max-w-3xl mx-auto mb-10" data-testid="simulator-progress">
            <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.22em] font-semibold text-ink/55 mb-3 px-1">
              <span className={step >= 1 ? "text-gold" : ""}>Localisation</span>
              <span className={step >= 2 ? "text-gold" : ""}>Logement</span>
              <span className={step >= 3 ? "text-gold" : ""}>Équipements</span>
            </div>
            <div className="h-1.5 bg-ink/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-gold via-gold to-amber-500 transition-all duration-500 ease-out rounded-full"
                style={{ width: `${(step / 3) * 100}%` }}
              />
            </div>
          </div>
        )}

        <div className="max-w-3xl mx-auto">
          {/* STEP 1, Postal code */}
          {step === 1 && (
            <Card testId="step-1">
              <StepHeader
                icon={MapPin}
                title="Où se situe votre bien ?"
                subtitle="Le code postal est essentiel pour ajuster les prix au marché local."
              />
              <div className="grid sm:grid-cols-2 gap-5 mt-8">
                <Field label="Code postal" required>
                  <input
                    data-testid="sim-postal"
                    type="text"
                    inputMode="numeric"
                    maxLength={5}
                    placeholder="35400"
                    value={form.postal_code}
                    onChange={(e) =>
                      update("postal_code", e.target.value.replace(/\D/g, ""))
                    }
                    className="sim-input text-2xl font-serif tracking-[0.4em] text-center"
                  />
                </Field>
                <Field label="Ville (facultatif)">
                  <input
                    data-testid="sim-city"
                    type="text"
                    placeholder="Saint-Malo"
                    value={form.custom_city}
                    onChange={(e) => update("custom_city", e.target.value)}
                    className="sim-input"
                  />
                </Field>
              </div>

              <p className="mt-6 text-xs text-ink/50 font-sans">
                💡 Codes pris en charge : 35400, 35260, 35430, 35800, 22100,
                22380, 22650, 22770, 56190 (Marzan), 56130 (La Roche-Bernard /
                Péaule), 56750 (Damgan / Ambon)… Côte d'Émeraude & Morbihan.
              </p>

              <Footer
                onNext={() => setStep(2)}
                disabled={!canNext1}
                step={step}
              />
            </Card>
          )}

          {/* STEP 2, Property */}
          {step === 2 && (
            <Card testId="step-2">
              <StepHeader
                icon={Home}
                title="Décrivez votre logement"
                subtitle="Quelques informations clés pour affiner le calcul."
              />

              <div className="mt-8 space-y-7">
                {/* Type */}
                <div>
                  <Label>Type de logement</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {PROPERTY_TYPES.map((p) => (
                      <button
                        key={p.value}
                        type="button"
                        data-testid={`sim-type-${p.value.toLowerCase()}`}
                        onClick={() => update("property_type", p.value)}
                        className={`group relative px-4 py-5 rounded-2xl border-2 transition-all text-left ${
                          form.property_type === p.value
                            ? "border-gold bg-gold/5 shadow-md"
                            : "border-ink/10 bg-white hover:border-gold/40"
                        }`}
                      >
                        <p.icon
                          size={20}
                          strokeWidth={1.6}
                          className={`mb-2 ${
                            form.property_type === p.value
                              ? "text-gold"
                              : "text-ink/60"
                          }`}
                        />
                        <span className="font-serif text-base text-ink block">
                          {p.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <Counter
                  icon={BedDouble}
                  label="Chambres"
                  value={form.bedrooms}
                  onChange={(v) => update("bedrooms", v)}
                  min={0}
                  max={10}
                  testId="sim-bedrooms"
                />
                <Counter
                  icon={Bath}
                  label="Salles de bain"
                  value={form.bathrooms}
                  onChange={(v) => update("bathrooms", v)}
                  min={0}
                  max={6}
                  testId="sim-bathrooms"
                />
                <Counter
                  icon={Users}
                  label="Capacité d'accueil"
                  value={form.capacity}
                  onChange={(v) => update("capacity", v)}
                  min={1}
                  max={20}
                  testId="sim-capacity"
                  suffix="pers."
                />
              </div>

              <Footer
                onNext={() => setStep(3)}
                onBack={() => setStep(1)}
                disabled={!canNext2}
                step={step}
              />
            </Card>
          )}

          {/* STEP 3, Amenities */}
          {step === 3 && (
            <Card testId="step-3">
              <StepHeader
                icon={Sparkles}
                title="Quels sont les atouts du logement ?"
                subtitle="Cochez tout ce qui s'applique. Plus il y en a, plus le potentiel grimpe."
              />

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-8">
                {AMENITIES.map((a) => {
                  const active = form.amenities.includes(a.key);
                  return (
                    <button
                      key={a.key}
                      type="button"
                      data-testid={`sim-amenity-${a.key}`}
                      onClick={() => toggleAmenity(a.key)}
                      className={`group relative px-4 py-4 rounded-2xl border-2 transition-all text-left flex items-center gap-3 ${
                        active
                          ? "border-gold bg-gold/5 shadow-md"
                          : "border-ink/10 bg-white hover:border-gold/40"
                      }`}
                    >
                      <a.icon
                        size={18}
                        strokeWidth={1.6}
                        className={
                          active
                            ? "text-gold shrink-0"
                            : "text-ink/55 shrink-0"
                        }
                      />
                      <span className="font-sans text-sm text-ink flex-1">
                        {a.label}
                      </span>
                      {a.premium && active && (
                        <Sparkles
                          size={12}
                          strokeWidth={2}
                          className="text-gold shrink-0"
                        />
                      )}
                      {active && (
                        <CheckCircle2
                          size={16}
                          strokeWidth={2}
                          className="text-gold absolute top-2 right-2"
                        />
                      )}
                    </button>
                  );
                })}
              </div>

              <Footer
                onNext={submitSim}
                onBack={() => setStep(2)}
                step={step}
                isFinal
                loading={loading}
              />
            </Card>
          )}

          {/* STEP 4, Result */}
          {step === 4 && result && (
            <ResultCard
              result={result}
              form={form}
              onRestart={restart}
              onContact={() => {
                const goToForm = () => {
                  const el = document.getElementById("contact-form");
                  if (el) {
                    const top =
                      el.getBoundingClientRect().top + window.scrollY - 90;
                    window.scrollTo({ top, behavior: "smooth" });
                  }
                };
                if (window.location.pathname.startsWith("/contact")) {
                  goToForm();
                } else {
                  navigate("/contact", {
                    state: { simResult: result, simForm: form },
                  });
                  // Scroll after the page mounts
                  setTimeout(goToForm, 350);
                }
              }}
            />
          )}
        </div>
      </div>

      <style>{`
        .sim-input {
          width: 100%;
          background: white;
          border: 1.5px solid rgba(26,43,60,0.12);
          border-radius: 0.875rem;
          padding: 0.95rem 1.1rem;
          font-family: 'Outfit', sans-serif;
          font-size: 0.95rem;
          color: rgb(26,43,60);
          transition: all 0.2s;
        }
        .sim-input:focus {
          outline: none;
          border-color: rgb(194 164 111);
          box-shadow: 0 0 0 4px rgba(194,164,111,0.12);
        }
      `}</style>
    </section>
  );
};

// ----- Subcomponents -----
const Card = ({ children, testId }) => (
  <div
    data-testid={testId}
    className="bg-white rounded-3xl p-7 sm:p-10 shadow-soft border border-ink/5 animate-in fade-in slide-in-from-bottom-4 duration-500"
  >
    {children}
  </div>
);

const StepHeader = ({ icon: Icon, title, subtitle }) => (
  <div className="flex items-start gap-4">
    <div className="w-12 h-12 rounded-2xl bg-gold/10 flex items-center justify-center shrink-0 mt-1">
      <Icon size={20} strokeWidth={1.6} className="text-gold" />
    </div>
    <div>
      <h3 className="font-serif text-2xl md:text-3xl text-ink leading-tight">
        {title}
      </h3>
      <p className="mt-2 text-sm text-ink/60 font-sans">{subtitle}</p>
    </div>
  </div>
);

const Field = ({ label, required, children }) => (
  <div>
    <Label>
      {label} {required && <span className="text-gold">*</span>}
    </Label>
    {children}
  </div>
);

const Label = ({ children }) => (
  <label className="block text-[10px] uppercase tracking-[0.22em] font-semibold text-sage mb-3">
    {children}
  </label>
);

const Counter = ({ icon: Icon, label, value, onChange, min, max, suffix, testId }) => (
  <div className="flex items-center justify-between gap-4 p-4 rounded-2xl bg-cream/40 border border-ink/8">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-white border border-ink/8 flex items-center justify-center">
        <Icon size={18} strokeWidth={1.6} className="text-ink/70" />
      </div>
      <span className="font-serif text-base text-ink">{label}</span>
    </div>
    <div className="flex items-center gap-2">
      <button
        type="button"
        data-testid={`${testId}-minus`}
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={value <= min}
        className="w-9 h-9 rounded-full bg-white border border-ink/15 flex items-center justify-center text-ink/70 hover:bg-gold hover:text-white hover:border-gold disabled:opacity-30 disabled:cursor-not-allowed transition-all font-serif text-lg leading-none"
      >
        −
      </button>
      <span
        className="font-serif text-2xl text-ink min-w-[3rem] text-center tabular-nums"
        data-testid={`${testId}-value`}
      >
        {value}
        {suffix && <span className="text-xs text-ink/55 ml-1">{suffix}</span>}
      </span>
      <button
        type="button"
        data-testid={`${testId}-plus`}
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={value >= max}
        className="w-9 h-9 rounded-full bg-white border border-ink/15 flex items-center justify-center text-ink/70 hover:bg-gold hover:text-white hover:border-gold disabled:opacity-30 disabled:cursor-not-allowed transition-all font-serif text-lg leading-none"
      >
        +
      </button>
    </div>
  </div>
);

const Footer = ({ onNext, onBack, disabled, isFinal, loading, step }) => (
  <div className="mt-9 flex items-center justify-between gap-3 border-t border-ink/8 pt-7">
    {onBack ? (
      <button
        type="button"
        data-testid={`sim-back-${step}`}
        onClick={onBack}
        className="inline-flex items-center gap-2 text-sm font-sans text-ink/65 hover:text-ink transition-colors"
      >
        <ArrowLeft size={14} strokeWidth={2} />
        Retour
      </button>
    ) : (
      <span />
    )}
    <button
      type="button"
      data-testid={`sim-next-${step}`}
      onClick={onNext}
      disabled={disabled || loading}
      className="btn-accent group disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {loading ? (
        <>
          <Loader2 size={14} strokeWidth={2} className="animate-spin mr-2" />
          Analyse en cours…
        </>
      ) : isFinal ? (
        <>
          <Sparkles size={14} strokeWidth={2} className="mr-2" />
          Lancer la simulation
        </>
      ) : (
        <>
          Continuer
          <ArrowRight
            size={14}
            strokeWidth={2}
            className="ml-2 group-hover:translate-x-1 transition-transform"
          />
        </>
      )}
    </button>
  </div>
);

// ----- Result -----
const ResultCard = ({ result, form, onRestart, onContact }) => {
  const monthlyNet = Math.round(result.monthly_gross_avg * 0.85);
  return (
    <div
      data-testid="step-4"
      className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500"
    >
      {/* Hero result */}
      <div className="bg-gradient-to-br from-ink via-ink to-[#0d1a26] text-white rounded-3xl p-8 md:p-12 grain-overlay relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[400px] h-[400px] bg-gold/25 rounded-full blur-[120px] pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-5">
            <Sparkles size={14} strokeWidth={2} className="text-gold" />
            <span className="overline text-gold">
              Simulation · {result.city}
            </span>
          </div>

          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            <div>
              <p className="text-[11px] uppercase tracking-[0.22em] text-white/55 mb-3">
                Tarif moyen / nuit
              </p>
              <p className="font-serif text-5xl md:text-6xl text-gold leading-none tabular-nums">
                {formatEUR(result.nightly_price_avg)}
              </p>
              <p
                className="mt-3 text-sm text-white/65 font-sans"
                data-testid="result-price-range"
              >
                Fourchette : {formatEUR(result.nightly_price_low)} -{" "}
                {formatEUR(result.nightly_price_high)}
              </p>
            </div>

            <div>
              <p className="text-[11px] uppercase tracking-[0.22em] text-white/55 mb-3">
                Revenu net mensuel estimé
              </p>
              <p
                className="font-serif text-5xl md:text-6xl text-white leading-none tabular-nums"
                data-testid="result-monthly-net"
              >
                {formatEUR(monthlyNet)}
              </p>
              <p className="mt-3 text-sm text-white/65 font-sans">
                après commission TERRE ET MER (15%)
              </p>
            </div>
          </div>

          {/* Metric stripe */}
          <div className="mt-10 pt-8 border-t border-white/10 grid grid-cols-3 gap-6">
            <Metric
              label="Occupation / an"
              value={`${result.occupancy_nights_year} nuits`}
            />
            <Metric
              label="Revenu brut annuel"
              value={formatEUR(result.yearly_gross_avg)}
            />
            <Metric
              label="Revenu net annuel"
              value={formatEUR(result.yearly_net_avg)}
              highlight
            />
          </div>
        </div>
      </div>

      {/* AI insight */}
      <div className="bg-white border border-gold/30 rounded-3xl p-7 md:p-9 shadow-soft">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-gold to-amber-600 flex items-center justify-center shrink-0">
            <Sparkles size={18} strokeWidth={1.6} className="text-white" />
          </div>
          <div className="flex-1">
            <p className="overline text-gold mb-2">Analyse IA personnalisée</p>
            <p
              data-testid="result-insight"
              className="font-sans text-base md:text-lg text-ink/85 leading-relaxed"
            >
              {result.insight}
            </p>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-cream rounded-3xl p-7 md:p-9 border border-ink/8 grid md:grid-cols-[1fr_auto] gap-5 items-center">
        <div>
          <p className="font-serif text-xl text-ink leading-snug">
            Envie d'aller plus loin ?
          </p>
          <p className="mt-2 text-sm text-ink/65 font-sans">
            Notre équipe vous propose une étude personnalisée gratuite, visite,
            photos pro, plan tarifaire saison par saison.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            data-testid="result-restart"
            onClick={onRestart}
            className="btn-ghost"
          >
            Refaire
          </button>
          <button
            type="button"
            data-testid="result-contact"
            onClick={onContact}
            className="btn-accent group"
          >
            Demander une étude
            <ArrowRight
              size={14}
              strokeWidth={2}
              className="ml-2 group-hover:translate-x-1 transition-transform"
            />
          </button>
        </div>
      </div>

      <p className="text-xs font-sans text-ink/50 leading-relaxed text-center px-4">
        Simulation indicative basée sur les données du marché breton, Côte
        d'Émeraude & Morbihan (2025-2026). Le potentiel réel dépend de la
        qualité des photos, des avis voyageurs et de la stratégie tarifaire
        mise en place.
      </p>
    </div>
  );
};

const Metric = ({ label, value, highlight }) => (
  <div>
    <p className="text-[10px] uppercase tracking-[0.22em] text-white/50 mb-2">
      {label}
    </p>
    <p
      className={`font-serif text-xl md:text-2xl tabular-nums ${
        highlight ? "text-gold" : "text-white"
      }`}
    >
      {value}
    </p>
  </div>
);

export default RevenueSimulator;
